/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: pages\dashboards\widgets\AuditLogWidget.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Tools module.
----------------------------------------------------------------*/


import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { Loader2, AlertCircle, FileText, Eye, X } from 'lucide-react';
import { format } from 'date-fns';
import { fetchAuditLogs } from '../../../modules/auditLogs/api/auditLogApi';
import { AuditLog } from '../../../modules/auditLogs/types/auditLog.types';
import { WidgetContext } from '../components/WidgetWrapper';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';

// Default configuration
const DEFAULT_LIMIT = 10;

interface AuditLogWidgetProps {
    config?: {
        limit?: number;
    };
    onConfigChange?: (newConfig: { limit?: number }) => void;
    isEditMode?: boolean;
    registerConfigHandler?: (handler: () => void) => void;
}

const actionColors: Record<string, string> = {
    CREATE: 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
    UPDATE: 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20',
    DELETE: 'text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
    LOGIN: 'text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20',
    LOGIN_FAILED: 'text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20',
    LOGOUT: 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/20',
};

// Helper to get a friendly resource name
const getResourceDisplay = (log: AuditLog): string => {
    // Try to get descriptive info from metadata
    if (log.metadata) {
        // Check for common descriptive fields in metadata
        if (log.metadata.name) return log.metadata.name;
        if (log.metadata.extension) return `Ext ${log.metadata.extension}`;
        if (log.metadata.description) return log.metadata.description;
        if (log.metadata.module) return log.metadata.module;
        if (log.metadata.entity) return log.metadata.entity;
    }

    // If resource is 'unknown' or empty, try to infer from action
    if (!log.resource || log.resource === 'unknown') {
        if (log.action === 'LOGIN' || log.action === 'LOGIN_FAILED') return 'Session';
        if (log.action === 'LOGOUT') return 'Session';
        return 'System';
    }

    return log.resource;
};

export const AuditLogWidget = ({ config, onConfigChange, isEditMode, registerConfigHandler }: AuditLogWidgetProps) => {
    const limit = config?.limit || DEFAULT_LIMIT;
    const widgetPrefs = useContext(WidgetContext);

    // State for detail modal
    const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

    // Config Refs for stable handler
    const configRef = useRef(config);
    const onConfigChangeRef = useRef(onConfigChange);
    const limitRef = useRef(limit);

    useEffect(() => {
        configRef.current = config;
        onConfigChangeRef.current = onConfigChange;
        limitRef.current = limit;
    }, [config, onConfigChange, limit]);

    // Register configuration handler ONCE
    useEffect(() => {
        if (registerConfigHandler) {
            registerConfigHandler(() => {
                const currentLimit = limitRef.current;
                const nextLimit = currentLimit === 5 ? 10 : currentLimit === 10 ? 20 : 5;
                if (onConfigChangeRef.current) {
                    onConfigChangeRef.current({ ...configRef.current, limit: nextLimit });
                }
            });
        }
    }, [registerConfigHandler]);

    // State for manual data fetching
    const [data, setData] = useState<{ data: AuditLog[] } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);

    // Track if data has been loaded at least once to avoid stale closure issues
    const hasLoadedRef = useRef(false);

    // Fetch logs manually
    const loadLogs = useCallback(async () => {
        try {
            // Only set loading on first load (when we haven't loaded data yet)
            if (!hasLoadedRef.current) setIsLoading(true);

            const response = await fetchAuditLogs({ limit, page: 1 });
            setData(response);
            setError(null);
            hasLoadedRef.current = true;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            console.error('Failed to load audit logs:', err);
            setError(err);
        } finally {
            setIsLoading(false);
        }
    }, [limit]);

    useEffect(() => {
        loadLogs();

        // Auto-refresh every 30s
        const intervalId = setInterval(loadLogs, 30000);
        return () => clearInterval(intervalId);
    }, [loadLogs]);

    // Retry handler
    const refetch = () => loadLogs();

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-full text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-destructive p-4 text-center">
                <AlertCircle className="h-6 w-6 mb-2" />
                <p className="text-xs">Failed to load audit logs</p>
                <button
                    onClick={() => refetch()}
                    className="mt-2 text-xs underline hover:text-destructive/80"
                >
                    Retry
                </button>
            </div>
        );
    }

    const logs = data?.data || [];

    if (logs.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                <FileText className="h-8 w-8 mb-2 opacity-20" />
                <p className="text-sm">No recent activity</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="flex-1 overflow-y-auto px-1">
                <div className="flex flex-col divide-y divide-border/40">
                    {logs.map((log) => {
                        const actionColorClass = actionColors[log.action] || 'text-foreground bg-muted';
                        const formattedDate = format(new Date(log.createdAt), 'MM/dd HH:mm');
                        const resourceDisplay = getResourceDisplay(log);

                        return (
                            <div
                                key={log.id}
                                className="flex items-center gap-2 py-1.5 first:pt-0 last:pb-0 hover:bg-muted/30 transition-colors px-1 rounded-sm text-xs group"
                            >
                                {/* Date/Time Column - Fixed Width */}
                                <span className="shrink-0 w-[72px] text-muted-foreground tabular-nums text-[11px]">
                                    {formattedDate}
                                </span>

                                {/* User Column - Fixed Width */}
                                <SimpleTooltip content={log.user?.fullName || log.user?.username || 'System'}>
                                    <span className="shrink-0 w-[55px] font-medium text-foreground truncate">
                                        {log.user?.username || 'System'}
                                    </span>
                                </SimpleTooltip>

                                {/* Action Badge - Fixed Width */}
                                <span className={`shrink-0 w-[80px] text-center px-1 py-0.5 rounded-[4px] text-[9px] font-semibold ${actionColorClass}`}>
                                    {log.action}
                                </span>

                                {/* Resource Column - Flexible */}
                                <SimpleTooltip content={resourceDisplay}>
                                    <span className="flex-1 text-foreground truncate">
                                        {resourceDisplay}
                                    </span>
                                </SimpleTooltip>

                                {/* IP Address Column - Fixed Width */}
                                <span className="shrink-0 w-[85px] text-muted-foreground tabular-nums text-right text-[10px]">
                                    {log.ipAddress || '-'}
                                </span>

                                {/* Detail Button */}
                                <SimpleTooltip content="View details">
                                    <button
                                        onClick={() => setSelectedLog(log)}
                                        className="shrink-0 w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                                    >
                                        <Eye className="h-3.5 w-3.5" />
                                    </button>
                                </SimpleTooltip>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Limit Indicator */}
            {isEditMode && (
                <div className="text-[10px] text-center text-muted-foreground pt-1 border-t mt-1">
                    Showing last {limit} (Click config to change)
                </div>
            )}

            {/* Detail Modal */}
            {selectedLog && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setSelectedLog(null)}>
                    <div
                        className="bg-background border rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[80vh] overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Modal Header */}
                        <div className="flex items-center justify-between px-4 py-3 border-b">
                            <h3 className="font-semibold text-sm">Audit Log Details</h3>
                            <button
                                onClick={() => setSelectedLog(null)}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-4 space-y-3 overflow-y-auto max-h-[60vh] text-sm">
                            <div className="grid grid-cols-[100px_1fr] gap-2">
                                <span className="text-muted-foreground">Date/Time:</span>
                                <span className="font-medium">{format(new Date(selectedLog.createdAt), 'MMM dd, yyyy HH:mm:ss')}</span>

                                <span className="text-muted-foreground">User:</span>
                                <span className="font-medium">{selectedLog.user?.fullName || selectedLog.user?.username || 'System'}</span>

                                <span className="text-muted-foreground">Action:</span>
                                <span className={`inline-block w-fit px-2 py-0.5 rounded text-xs font-semibold ${actionColors[selectedLog.action] || 'bg-muted'}`}>
                                    {selectedLog.action}
                                </span>

                                <span className="text-muted-foreground">Resource:</span>
                                <span className="font-medium">{selectedLog.resource || 'System'}</span>

                                {selectedLog.resourceId && (
                                    <>
                                        <span className="text-muted-foreground">Resource ID:</span>
                                        <span className="font-mono text-xs break-all">{selectedLog.resourceId}</span>
                                    </>
                                )}

                                <span className="text-muted-foreground">IP Address:</span>
                                <span className="font-mono">{selectedLog.ipAddress || '-'}</span>

                                {selectedLog.userAgent && (
                                    <>
                                        <span className="text-muted-foreground">User Agent:</span>
                                        <span className="text-xs break-all text-muted-foreground">{selectedLog.userAgent}</span>
                                    </>
                                )}
                            </div>

                            {/* Metadata Section */}
                            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                                <div className="pt-3 border-t">
                                    <span className="text-muted-foreground text-xs font-medium">Additional Details:</span>
                                    <pre className="mt-2 p-2 bg-muted/50 rounded text-xs overflow-x-auto">
                                        {JSON.stringify(selectedLog.metadata, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
